import React, { useCallback, useState, useEffect } from 'react';
import {
    Dialog,
    DialogTrigger,
    DialogSurface,
    DialogBody,
    DialogTitle,
    DialogContent,
    DialogActions,
    Input,
    Checkbox,
    Button,
    DialogOpenChangeEvent,
    DialogOpenChangeData,
    Dropdown,
    Option,
    Label,
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import {
    upsertFlightNumbers,
    selectFlightNumbersUpsertFeedback,
    resetUpsertFeedback,
} from '../../../../../store/features/flightNumbers/flightNumbersSlice';
import { useAppDispatch, useSelector } from '../../../../../store/hooks';
import { useFlightNumberDialogStyles } from './FlightNumberDialog.styles'
import { HaulType } from '../../../../../model';
import { getHaulTypeLabels } from '../../../../DepartureBoardPage/utils/departureBoardFiltersUtils';
import {
    FlightHaulType,
} from '../../../../../store/features/departureBoard/departureBoardSlice';

interface Props {
    open: boolean;
    flightNumber: string;
    isPermanent: boolean;
    haulType: HaulType;
    onSaved?: () => void;
    onOpenChange: (
        e: DialogOpenChangeEvent,
        data: DialogOpenChangeData
    ) => void;
}

const flightNumberPrefix = 'BA';
const flightNumberRegex = /^BA[A-Za-z0-9]{0,8}$/; // BA + up to 8 alphanumeric chars (total10)

const normalizeFlightNumberInput = (raw: string): string => {
    const compact = raw.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 10);
    if (!compact) {
        return '';
    }

    const startsWithNumber = /^\d/.test(compact);
    if (!startsWithNumber) {
        return compact;
    }

    const withPrefix = compact.startsWith(flightNumberPrefix)
        ? compact
        : `${flightNumberPrefix}${compact}`;

    return withPrefix.slice(0, 10);
};

const FlightNumberDialogAction: React.FC<Props> = ({
    open,
    flightNumber,
    isPermanent,
    haulType: initialHaulType,
    onSaved,
    onOpenChange,
}) => {
    const [value, setValue] = useState(flightNumber);
    const [permanent, setPermanent] = useState(isPermanent);
    const [haulType, setHaulType] = useState<HaulType>(initialHaulType);
    const [localError, setLocalError] = useState<string>('');
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const feedback = useSelector(selectFlightNumbersUpsertFeedback);
    const styles = useFlightNumberDialogStyles();
    const haulTypeLabels = getHaulTypeLabels(t);

    useEffect(() => {
        setValue(flightNumber);
        setPermanent(isPermanent);
        setHaulType(initialHaulType);
        setLocalError('');
        if (open) {
            dispatch(resetUpsertFeedback());
        }
    }, [flightNumber, isPermanent, initialHaulType, open, dispatch]);

    const validate = useCallback(
        (val: string) => {
            if (!val) {
                return t(
                    'Validation_FlightNumber_Required',
                    'Flight number is required'
                );
            }
            if (!flightNumberRegex.test(val)) {
                return t(
                    'Validation_FlightNumber_Format',
                    'Flight number must start with BA followed by alphanumeric characters'
                );
            }
            return '';
        },
        [t]
    );

    const onChange = useCallback(
        (_: any, d: { value: string }) => {
            const sanitized = normalizeFlightNumberInput(d.value);
            setValue(sanitized);
            setLocalError(validate(sanitized));
        },
        [validate]
    );

    const onSave = useCallback(
        async (e: React.MouseEvent) => {
            const trimmed = value.trim();
            const validationMessage = validate(trimmed);
            if (validationMessage) {
                setLocalError(validationMessage);
                return;
            }
            try {
                const result = await dispatch(
                    upsertFlightNumbers({
                        flightNumber: trimmed,
                        isPermanent: permanent,
                        haulType,
                    })
                ).unwrap();

                if (result.status === 'success') {
                    onSaved?.();
                    onOpenChange(e as any, {
                        open: false,
                        type: 'triggerClick',
                        event: e as any,
                    });
                } else if (result.status === 'duplicate') {
                    setLocalError(
                        result.message ||
                        t('CreateOrUpdateFlightNumberDialog_Duplicate', 'Flight number already exists')
                    );
                }
            } catch {
                setLocalError(t('CreateOrUpdateFlightNumberDialog_Error_Message', 'Failed to update flight number.'));
            }
        },
        [value, permanent, haulType, validate, dispatch, onSaved, onOpenChange, t]
    );

    // Combine local validation error with feedback from upsert action
    const effectiveError =
        localError ||
        (feedback.status === 'duplicate'
            ? feedback.message || t('CreateOrUpdateFlightNumberDialog_Duplicate', 'Flight number already exists') : '');

    const isValid = !localError && !!value && flightNumberRegex.test(value);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogSurface className={styles.flightNumberDialog}>
                <DialogBody>
                    <DialogTitle
                        action={
                            <DialogTrigger action="close">
                                <Button
                                    appearance="subtle"
                                    aria-label="close"
                                    icon={<Dismiss24Regular />}
                                />
                            </DialogTrigger>
                        }
                    >
                        {flightNumber
                            ? t('EditFlightNumberDialog_Title', 'Edit Flight Number')
                            : t('CreateOrUpdateFlightNumberDialog_Title')}
                    </DialogTitle>
                    <DialogContent>
                        <div className={styles.contentContainer}>
                            <div className={styles.inputWrapper}>
                                <Label>{t('CreateOrUpdateFlightNumberDialog_InputLabel')}</Label>
                                <Input
                                    value={value}
                                    disabled={!!flightNumber}
                                    onChange={onChange}
                                    maxLength={10}
                                    placeholder={t('CreateOrUpdateFlightNumberDialog_Input_Placeholder')}
                                    aria-invalid={!!effectiveError}
                                />
                                {effectiveError && (
                                    <div className={styles.errorMessage}>
                                        {effectiveError}
                                    </div>
                                )}
                            </div>
                            <div className={styles.inputWrapper}>
                                <Label>{t('TableHeader_HaulType', 'SH/LH')}</Label>
                                <Dropdown
                                    value={haulTypeLabels[haulType]}
                                    selectedOptions={[haulType]}
                                    onOptionSelect={(_, data) => setHaulType(data.optionValue as HaulType)}
                                >
                                    <Option value={FlightHaulType.Short}>{haulTypeLabels.Short}</Option>
                                    <Option value={FlightHaulType.Long}>{haulTypeLabels.Long}</Option>
                                </Dropdown>
                            </div>
                            <Checkbox
                                checked={permanent}
                                label={t('CreateOrUpdateFlightNumberDialog_Checkbox_Label')}
                                onChange={(_, d) => setPermanent(!!d.checked)}
                            />
                        </div>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            appearance="primary"
                            disabled={!isValid}
                            onClick={onSave}
                        >
                            {t('Button_Common_Save')}
                        </Button>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
};

export default FlightNumberDialogAction;
